import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

import FloatingAdminMenu from "./FloatMenu";
import fondo from "../assets/fondo1.jpg";

import {
  FiLock, FiEye, FiEyeOff, FiLogOut, FiCpu,
  FiSave, FiRefreshCw, FiFileText, FiMessageCircle,
  FiEdit3, FiSettings, FiCheck, FiAlertCircle,
  FiZap, FiChevronRight, FiChevronLeft, FiArrowRight,
  FiMenu, FiX,
} from "react-icons/fi";

/* ─────────────────────────────────────
   API & AUTH
───────────────────────────────────── */
const API      = "https://aldanaselena.com/bot.php";
const PASSWORD = "aldana.admin";

const initialForm = {
  nombre: "", descripcion: "", personalidad: "", tono: "",
  mensaje_central: "", reglas: "", estructura: "", informacion: "",
  modelo: "gpt-4o-mini", temperatura: 0.5, max_tokens: 120, activo: 1,
};

/* ─────────────────────────────────────
   PALETA
───────────────────────────────────── */
const C = {
  gold:       "#C9A96E",
  goldDim:    "#8B6E47",
  goldSub:    "rgba(201,169,110,0.10)",
  goldBorder: "rgba(201,169,110,0.18)",
  goldHov:    "rgba(201,169,110,0.30)",
  bg:         "#0A0806",
  card:       "#100C09",
  cardHov:    "#16110D",
  border:     "rgba(201,169,110,0.13)",
  text:       "#EDE0D0",
  textDim:    "rgba(237,224,208,0.50)",
  textFaint:  "rgba(237,224,208,0.25)",
  green:      "#6DBF8F",
  greenBg:    "rgba(61,107,79,0.15)",
  red:        "#E07A7A",
  redBg:      "rgba(224,122,122,0.12)",
};

/* ─────────────────────────────────────
   STEPS
───────────────────────────────────── */
const STEPS = [
  {
    key: "nombre", label: "Nombre del agente",
    icon: <FiCpu size={18} />, required: true,
    hint: "El nombre con el que se identificará el asistente. Elegí algo representativo y coherente con tu marca.",
    placeholder: "Ej: Luna, Sofía, Asistente de Aldana...",
    rows: 1, type: "input",
  },
  {
    key: "descripcion", label: "Descripción general",
    icon: <FiFileText size={18} />, required: true,
    hint: "Una descripción breve del rol del agente: qué hace, para quién trabaja y cuál es su propósito principal.",
    placeholder: "Ej: Soy el asistente virtual de Aldana Selena, artista especializada en línea fina...",
    rows: 4, type: "textarea",
  },
  {
    key: "personalidad", label: "Personalidad",
    icon: <FiMessageCircle size={18} />, required: true,
    hint: "Define cómo se comporta y se expresa el agente. Describí rasgos de carácter: amigable, profesional, empático, directo, etc.",
    placeholder: "Ej: Soy cálida, empática y profesional. Me gusta generar confianza desde el primer mensaje...",
    rows: 5, type: "textarea",
  },
  {
    key: "tono", label: "Tono de comunicación",
    icon: <FiEdit3 size={18} />, required: true,
    hint: "El estilo de escritura. Podés combinarlo con ejemplos directos de cómo hablar y qué evitar.",
    placeholder: "Ej: Cercano pero profesional. Uso vocabulario accesible, evito tecnicismos...",
    rows: 4, type: "textarea",
  },
  {
    key: "mensaje_central", label: "Mensaje central",
    icon: <FiZap size={18} />, required: true,
    hint: "La idea o propuesta de valor que el agente debe transmitir siempre. Es el eje de todas sus respuestas.",
    placeholder: "Ej: Ayudo a las personas a dar el paso hacia su primer o próximo tatuaje con confianza...",
    rows: 4, type: "textarea",
  },
  {
    key: "reglas", label: "Reglas de conducta",
    icon: <FiSettings size={18} />, required: true,
    hint: "Lo que el agente puede y no puede hacer. Restricciones, límites de respuesta y comportamientos obligatorios.",
    placeholder: "Ej: Nunca compartir precios exactos. No hablar de temas ajenos al estudio...",
    rows: 5, type: "textarea",
  },
  {
    key: "estructura", label: "Estructura de respuesta",
    icon: <FiFileText size={18} />, required: false,
    hint: "Opcional. Indica cómo debe organizarse cada respuesta: longitud, formato, si usa listas, etc.",
    placeholder: "Ej: Respuestas cortas de 2-3 oraciones. Siempre termino con una pregunta...",
    rows: 4, type: "textarea",
  },
  {
    key: "informacion", label: "Información contextual",
    icon: <FiFileText size={18} />, required: false,
    hint: "Opcional. Datos concretos: horarios, servicios, precios, ubicación, links, redes sociales, etc.",
    placeholder: "Ej: El estudio está en Mar del Plata. Trabajamos lunes a sábado de 10 a 18hs...",
    rows: 6, type: "textarea",
  },
];

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const pageVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 48 : -48 }),
  center: { opacity: 1, x: 0, transition: { duration: 0.38, ease: [0.25, 0.1, 0.25, 1] } },
  exit: (dir) => ({ opacity: 0, x: dir < 0 ? 48 : -48, transition: { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] } }),
};

/* ─────────────────────────────────────
   SUB-COMPONENTES
───────────────────────────────────── */
const GoldLine = () => (
  <div className="h-px my-6"
       style={{ background: `linear-gradient(90deg, transparent, ${C.gold}35, transparent)` }} />
);

const Label = ({ children, className = "" }) => (
  <p className={`text-[10px] tracking-[0.4em] uppercase font-light ${className}`}
     style={{ color: C.goldDim }}>
    {children}
  </p>
);

/* Input/Textarea con focus animado */
const StyledField = ({ value, onChange, name, placeholder, rows, type }) => {
  const [focused, setFocused] = useState(false);

  const sharedStyle = {
    background:  C.bg,
    color:       C.text,
    border:      `1px solid ${focused ? C.gold : C.border}`,
    transition:  "border-color 0.25s, box-shadow 0.25s",
    lineHeight:  1.75,
    outline:     "none",
    width:       "100%",
    fontWeight:  300,
    fontSize:    "0.875rem",
    boxShadow:   focused ? `0 0 0 3px rgba(201,169,110,0.07)` : "none",
  };

  if (type === "input") {
    return (
      <div className="relative">
        <input
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{ ...sharedStyle, height: "3rem", borderRadius: "0.75rem", padding: "0 1rem" }}
        />
        <AnimatePresence>
          {focused && (
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              className="absolute bottom-0 left-4 right-4 h-[1px] origin-left"
              style={{ background: `linear-gradient(90deg, ${C.gold}, transparent)` }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...sharedStyle,
          borderRadius: "0.75rem",
          padding: "0.875rem 1rem",
          resize: "vertical",
          minHeight: "80px",
        }}
      />
      <AnimatePresence>
        {focused && (
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            className="absolute bottom-0 left-4 right-4 h-[1px] origin-left"
            style={{ background: `linear-gradient(90deg, ${C.gold}, transparent)` }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* Barra de progreso global */
const ProgressBar = ({ current, total }) => (
  <div className="relative h-[2px] rounded-full overflow-hidden" style={{ background: C.border }}>
    <motion.div
      className="absolute left-0 top-0 h-full rounded-full"
      style={{ background: `linear-gradient(90deg, ${C.goldDim}, ${C.gold})` }}
      animate={{ width: `${((current + 1) / total) * 100}%` }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    />
  </div>
);

/* Sidebar desktop */
const StepSidebar = ({ steps, current, completed, onJump }) => (
  <div className="hidden xl:flex flex-col gap-1 w-52 flex-shrink-0 pt-1">
    {steps.map((s, i) => {
      const done   = completed.has(s.key);
      const active = i === current;
      return (
        <motion.button
          key={s.key}
          onClick={() => onJump(i)}
          whileHover={{ x: 3 }}
          transition={{ duration: 0.18 }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all"
          style={{
            background: active ? C.goldSub : "transparent",
            border: `1px solid ${active ? C.goldBorder : "transparent"}`,
          }}
        >
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
            style={{
              background: done ? C.gold : active ? C.goldSub : C.border,
              color: done ? "#0A0806" : active ? C.gold : C.textFaint,
            }}
          >
            {done ? <FiCheck size={9} /> : <span style={{ fontSize: "9px" }}>{i + 1}</span>}
          </div>
          <div className="overflow-hidden min-w-0">
            <p className="text-[11px] font-light truncate"
               style={{ color: active ? C.gold : done ? C.textDim : C.textFaint }}>
              {s.label}
            </p>
            {!s.required && (
              <p className="text-[9px] tracking-widest uppercase" style={{ color: C.textFaint }}>
                Opcional
              </p>
            )}
          </div>
        </motion.button>
      );
    })}
  </div>
);

/* Drawer lateral para mobile */
const MobileStepDrawer = ({ steps, current, completed, onJump, open, onClose }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-40"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
        />
        <motion.div
          initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 34 }}
          className="fixed left-0 top-0 bottom-0 z-50 flex flex-col w-[280px] shadow-2xl"
          style={{ background: C.card, borderRight: `1px solid ${C.border}` }}
        >
          {/* Header drawer */}
          <div className="flex items-center justify-between px-5 py-4 border-b"
               style={{ borderColor: C.border }}>
            <Label>Pasos del wizard</Label>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: C.goldSub, color: C.gold }}
            >
              <FiX size={14} />
            </motion.button>
          </div>

          {/* Steps list */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {steps.map((s, i) => {
              const done   = completed.has(s.key);
              const active = i === current;
              return (
                <motion.button
                  key={s.key}
                  onClick={() => { onJump(i); onClose(); }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left"
                  style={{
                    background: active ? C.goldSub : "transparent",
                    border: `1px solid ${active ? C.goldBorder : "transparent"}`,
                  }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: done ? C.gold : active ? C.goldSub : C.border,
                      color: done ? "#0A0806" : active ? C.gold : C.textFaint,
                    }}
                  >
                    {done ? <FiCheck size={10} /> : <span style={{ fontSize: "9px" }}>{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-light truncate"
                       style={{ color: active ? C.gold : done ? C.textDim : C.textFaint }}>
                      {s.label}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {!s.required && (
                        <span className="text-[9px] tracking-widest uppercase" style={{ color: C.textFaint }}>
                          Opcional
                        </span>
                      )}
                      {done && (
                        <span className="text-[9px] tracking-widest uppercase" style={{ color: C.green }}>
                          ✓ Completado
                        </span>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

/* Toggle activo */
const ActiveToggle = ({ value, onChange }) => (
  <div className="flex items-center justify-between px-4 sm:px-5 py-4 rounded-xl"
       style={{ background: C.card, border: `1px solid ${C.border}` }}>
    <div className="flex-1 min-w-0 mr-4">
      <Label>Estado del agente</Label>
      <p className="text-xs font-light mt-1 truncate" style={{ color: value ? C.green : C.textFaint }}>
        {value ? "Activo — respondiendo consultas" : "Pausado — no responde"}
      </p>
    </div>
    <div className="flex items-center gap-2.5 flex-shrink-0">
      <motion.div
        animate={{ scale: value ? [1, 1.3, 1] : 1, opacity: value ? [0.6, 1, 0.6] : 0.3 }}
        transition={{ duration: 2, repeat: value ? Infinity : 0 }}
        className="w-2 h-2 rounded-full"
        style={{ background: value ? C.green : C.goldDim }}
      />
      <motion.button
        onClick={onChange}
        className="relative w-12 h-6 rounded-full flex-shrink-0"
        style={{ background: value ? "rgba(61,107,79,0.4)" : "rgba(201,169,110,0.1)" }}
      >
        <motion.div
          animate={{ x: value ? 24 : 2 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="absolute top-1 w-4 h-4 rounded-full"
          style={{ background: value ? C.green : C.goldDim }}
        />
      </motion.button>
    </div>
  </div>
);

/* Chips de campos completados — versión compacta responsive */
const CompletionChips = ({ steps, completed }) => (
  <div className="flex flex-wrap gap-1.5">
    {steps.map((s) => (
      <motion.div
        key={s.key}
        animate={{ opacity: completed.has(s.key) ? 1 : 0.32 }}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
        style={{
          background: completed.has(s.key) ? "rgba(201,169,110,0.08)" : "transparent",
          border: `1px solid ${completed.has(s.key) ? C.goldBorder : C.border}`,
        }}
      >
        {completed.has(s.key)
          ? <FiCheck size={8} style={{ color: C.gold }} />
          : <div className="w-1 h-1 rounded-full" style={{ background: C.textFaint }} />
        }
        <span className="text-[9px] tracking-widest uppercase font-light"
              style={{ color: completed.has(s.key) ? C.goldDim : C.textFaint }}>
          {s.label.split(" ")[0]}
        </span>
      </motion.div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminAgenteIA = () => {
  const [form,          setForm]          = useState(initialForm);
  const [isAuth,        setIsAuth]        = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [errorAuth,     setErrorAuth]     = useState("");
  const [showMenu,      setShowMenu]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [saveState,     setSaveState]     = useState("idle");
  const [drawerOpen,    setDrawerOpen]    = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [direction,   setDirection]   = useState(1);
  const [completed,   setCompleted]   = useState(new Set());

  const totalSteps = STEPS.length;
  const step       = STEPS[currentStep];
  const isLast     = currentStep === totalSteps - 1;
  const isFirst    = currentStep === 0;

  /* ── Auth ── */
  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") setIsAuth(true);
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true);
      localStorage.setItem("admin_auth", "true");
      setErrorAuth("");
    } else {
      setErrorAuth("Contraseña incorrecta");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuth(false);
  };

  /* ── Fetch ── */
  const fetchAgent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setForm(res.data[0]);
        const done = new Set();
        STEPS.forEach((s) => { if (res.data[0][s.key]) done.add(s.key); });
        setCompleted(done);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAuth) fetchAgent(); }, [isAuth, fetchAgent]);

  /* ── Handlers ── */
  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const markCompleted = (key) => setCompleted((prev) => new Set([...prev, key]));

  const goNext = () => {
    if (form[step.key]?.trim()) markCompleted(step.key);
    if (!isLast) { setDirection(1); setCurrentStep((p) => p + 1); }
  };

  const goPrev = () => {
    if (!isFirst) { setDirection(-1); setCurrentStep((p) => p - 1); }
  };

  const jumpTo = (i) => {
    setDirection(i > currentStep ? 1 : -1);
    setCurrentStep(i);
  };

  /* ── Save ── */
  const handleSave = async () => {
    setSaveState("saving");
    try {
      await axios.put(`${API}?id=${form.id}`, form);
      setSaveState("success");
      setCompleted(new Set(STEPS.map((s) => s.key)));
      setTimeout(() => setSaveState("idle"), 2800);
    } catch {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 2800);
    }
  };

  /* ── Save button map ── */
  const saveBtnMap = {
    idle:    { bg: C.gold,    text: "#0A0806", label: "Guardar todo",     icon: <FiSave size={14} /> },
    saving:  { bg: C.goldDim, text: C.text,    label: "Guardando...",    icon: <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}><FiRefreshCw size={14} /></motion.div> },
    success: { bg: "#3D6B4F", text: C.text,    label: "¡Guardado!",     icon: <FiCheck size={14} /> },
    error:   { bg: "#7B3B3B", text: C.text,    label: "Error al guardar", icon: <FiAlertCircle size={14} /> },
  };
  const saveBtn = saveBtnMap[saveState];

  const requiredSteps   = STEPS.filter((s) => s.required);
  const requiredDone    = requiredSteps.filter((s) => completed.has(s.key)).length;
  const allRequiredDone = requiredDone === requiredSteps.length;

  /* ── Keyboard nav ── */
  useEffect(() => {
    const handler = (e) => {
      if (!isAuth || loading || drawerOpen) return;
      if (e.key === "ArrowRight" && !isLast) goNext();
      if (e.key === "ArrowLeft" && !isFirst) goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAuth, loading, drawerOpen, currentStep, isLast, isFirst]);

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div className="relative min-h-screen overflow-x-hidden" style={{ background: C.bg }}>

      {/* Fondo */}
      <img src={fondo} alt="" aria-hidden
           className="absolute inset-0 w-full h-full object-cover opacity-[0.06] pointer-events-none select-none" />
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(ellipse at 25% 0%, rgba(201,169,110,0.07) 0%, transparent 55%)" }} />
      <div className="fixed top-0 left-0 right-0 h-[1px] z-50"
           style={{ background: `linear-gradient(90deg, transparent, ${C.gold}55, transparent)` }} />

      {/* ─────── LOGIN ─────── */}
      <AnimatePresence>
        {!isAuth && (
          <motion.div
            className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 backdrop-blur-md"
                 style={{ background: "rgba(10,8,6,0.93)" }} />

            <motion.div
              initial={{ scale: 0.88, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.88, y: 24, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative z-10 w-full max-w-sm p-6 sm:p-8 rounded-2xl shadow-2xl"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="absolute top-0 left-8 right-8 h-[1px]"
                   style={{ background: `linear-gradient(90deg, transparent, ${C.gold}40, transparent)` }} />

              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: C.goldSub, color: C.gold }}>
                  <FiLock size={16} />
                </div>
                <div>
                  <Label>Acceso restringido</Label>
                  <h2 className="text-sm font-light tracking-[0.2em] mt-0.5" style={{ color: C.text }}>
                    AGENTE IA ADMIN
                  </h2>
                </div>
              </div>

              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder="Contraseña"
                  className="w-full rounded-xl px-4 py-3 pr-12 text-sm font-light"
                  style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, outline: "none" }}
                />
                <button
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: C.goldDim }}
                >
                  {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                </button>
              </div>

              <AnimatePresence>
                {errorAuth && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2 text-xs mb-4"
                    style={{ color: C.red }}
                  >
                    <FiAlertCircle size={11} /> {errorAuth}
                  </motion.p>
                )}
              </AnimatePresence>

              <motion.button
                whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }}
                onClick={handleLogin}
                className="w-full py-3 rounded-xl text-sm font-light tracking-[0.25em] uppercase"
                style={{ background: C.gold, color: "#0A0806" }}
              >
                Ingresar
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menú flotante admin */}
      {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

      {/* Drawer mobile */}
      {isAuth && (
        <MobileStepDrawer
          steps={STEPS}
          current={currentStep}
          completed={completed}
          onJump={jumpTo}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {/* ─────── PANEL PRINCIPAL ─────── */}
      {isAuth && (
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-28 sm:pt-32 pb-20 sm:pb-28">
          {/* ── TOPBAR MOBILE ── */}
          <div className="flex xl:hidden items-center justify-between mb-6">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: C.goldSub, border: `1px solid ${C.goldBorder}`, color: C.gold }}
            >
              <FiMenu size={15} />
              <span className="text-[11px] tracking-[0.2em] uppercase font-light hidden sm:inline">
                Pasos
              </span>
              <span className="text-[10px] font-light" style={{ color: C.textDim }}>
                {currentStep + 1}/{totalSteps}
              </span>
            </motion.button>

            <div className="flex items-center gap-2">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={fetchAgent}
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ border: `1px solid ${C.border}`, color: C.gold }}
              >
                <motion.div
                  animate={loading ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 1, repeat: loading ? Infinity : 0, ease: "linear" }}
                >
                  <FiRefreshCw size={14} />
                </motion.div>
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleLogout}
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ border: "1px solid rgba(224,122,122,0.2)", color: C.red }}
              >
                <FiLogOut size={14} />
              </motion.button>
            </div>
          </div>

          {/* ── HEADER DESKTOP ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden xl:flex flex-row items-start justify-between gap-6 mb-10"
          >
            <div>
              <Label>Panel de administración</Label>
              <h1 className="text-4xl xl:text-5xl font-light tracking-[0.22em] mt-1"
                  style={{ color: C.gold }}>
                AGENTE IA
              </h1>
              <p className="text-xs font-light mt-2" style={{ color: C.textFaint }}>
                Configurá paso a paso cómo se comporta tu asistente
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end gap-1">
                <Label>Requeridos</Label>
                <p className="text-sm font-light" style={{ color: allRequiredDone ? C.green : C.gold }}>
                  {requiredDone} / {requiredSteps.length}
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={fetchAgent}
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ border: `1px solid ${C.border}`, color: C.gold }}
              >
                <motion.div
                  animate={loading ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 1, repeat: loading ? Infinity : 0, ease: "linear" }}
                >
                  <FiRefreshCw size={15} />
                </motion.div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ border: "1px solid rgba(224,122,122,0.2)", color: C.red }}
              >
                <FiLogOut size={15} />
              </motion.button>
            </div>
          </motion.div>

          {/* ── HEADER MOBILE (título compacto) ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="xl:hidden mb-6"
          >
            <Label>Panel de administración</Label>
            <div className="flex items-baseline gap-3 mt-1">
              <h1 className="text-2xl sm:text-3xl font-light tracking-[0.2em]" style={{ color: C.gold }}>
                AGENTE IA
              </h1>
              <span className="text-xs font-light" style={{ color: allRequiredDone ? C.green : C.goldDim }}>
                {requiredDone}/{requiredSteps.length} req.
              </span>
            </div>
          </motion.div>

          {/* ── LOADING ── */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="flex flex-col items-center py-24 sm:py-32 gap-4"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="w-10 h-10 rounded-full"
                  style={{ border: `1px solid ${C.border}`, borderTopColor: C.gold }}
                />
                <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: C.goldDim }}>
                  Cargando agente
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── WIZARD ── */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              {/* Barra de progreso */}
              <div className="mb-6 sm:mb-8">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-[10px] tracking-[0.3em] uppercase font-light" style={{ color: C.textFaint }}>
                    {step.label}
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-light" style={{ color: C.goldDim }}>
                    {Math.round(((currentStep + 1) / totalSteps) * 100)}%
                  </p>
                </div>
                <ProgressBar current={currentStep} total={totalSteps} />
              </div>

              {/* Layout: sidebar + contenido */}
              <div className="flex gap-6 xl:gap-8">

                {/* Sidebar desktop */}
                <StepSidebar steps={STEPS} current={currentStep} completed={completed} onJump={jumpTo} />

                {/* Contenido */}
                <div className="flex-1 min-w-0">

                  {/* Card del paso */}
                  <div
                    className="rounded-2xl p-5 sm:p-7 md:p-8 overflow-hidden relative"
                    style={{ background: C.card, border: `1px solid ${C.border}` }}
                  >
                    <div className="absolute top-0 left-6 right-6 h-[1px]"
                         style={{ background: `linear-gradient(90deg, transparent, ${C.gold}35, transparent)` }} />

                    <AnimatePresence custom={direction} mode="wait">
                      <motion.div
                        key={step.key}
                        custom={direction}
                        variants={pageVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                      >
                        {/* Ícono + título */}
                        <div className="flex items-start gap-3 sm:gap-4 mb-5">
                          <motion.div
                            initial={{ scale: 0.7, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.4 }}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: C.goldSub, color: C.gold }}
                          >
                            {step.icon}
                          </motion.div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-wrap">
                              <h2 className="text-lg sm:text-xl md:text-2xl font-light leading-snug" style={{ color: C.text }}>
                                {step.label}
                              </h2>
                              <span
                                className="text-[9px] tracking-[0.3em] uppercase px-2 py-1 rounded-full flex-shrink-0"
                                style={{
                                  background: step.required ? "rgba(201,169,110,0.1)" : "rgba(61,107,79,0.12)",
                                  color:      step.required ? C.gold : C.green,
                                  border:     `1px solid ${step.required ? C.goldBorder : "rgba(61,107,79,0.25)"}`,
                                }}
                              >
                                {step.required ? "Requerido" : "Opcional"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Hint */}
                        <motion.div
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15, duration: 0.4 }}
                          className="mb-5 sm:mb-6 px-3.5 sm:px-4 py-3 rounded-xl"
                          style={{ background: "rgba(201,169,110,0.05)", border: `1px solid ${C.goldBorder}` }}
                        >
                          <p className="text-xs sm:text-sm font-light leading-relaxed" style={{ color: C.textDim }}>
                            {step.hint}
                          </p>
                        </motion.div>

                        {/* Campo */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.4 }}
                        >
                          <StyledField
                            name={step.key}
                            value={form[step.key]}
                            onChange={handleChange}
                            placeholder={step.placeholder}
                            rows={step.rows}
                            type={step.type}
                          />
                        </motion.div>

                        {/* Contador */}
                        <motion.p
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                          className="text-[10px] text-right mt-2 font-light"
                          style={{ color: C.textFaint }}
                        >
                          {(form[step.key] || "").length} caracteres
                        </motion.p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* ── NAVEGACIÓN ── */}
                  <div className="flex items-center justify-between mt-4 sm:mt-5 gap-3">

                    {/* Anterior */}
                    <motion.button
                      whileHover={!isFirst ? { x: -3 } : {}}
                      whileTap={!isFirst ? { scale: 0.96 } : {}}
                      onClick={goPrev}
                      disabled={isFirst}
                      className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-[11px] sm:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-all"
                      style={{
                        border:  `1px solid ${isFirst ? "transparent" : C.border}`,
                        color:   isFirst ? C.textFaint : C.textDim,
                        cursor:  isFirst ? "default" : "pointer",
                        opacity: isFirst ? 0.4 : 1,
                      }}
                    >
                      <FiChevronLeft size={13} />
                      <span className="hidden sm:inline">Anterior</span>
                    </motion.button>

                    {/* Dots de navegación — ocultos en xs para no saturar */}
                    <div className="hidden sm:flex items-center gap-1.5">
                      {STEPS.map((s, i) => (
                        <motion.button
                          key={s.key}
                          onClick={() => jumpTo(i)}
                          animate={{ scale: i === currentStep ? 1.4 : 1 }}
                          className="w-1.5 h-1.5 rounded-full transition-all"
                          style={{
                            background: completed.has(s.key) ? C.gold
                                      : i === currentStep    ? C.goldDim
                                      : C.border,
                          }}
                        />
                      ))}
                    </div>

                    {/* Progreso numérico en mobile cuando no caben dots */}
                    <p className="sm:hidden text-[10px] font-light" style={{ color: C.textFaint }}>
                      {currentStep + 1} / {totalSteps}
                    </p>

                    {/* Siguiente / Finalizar */}
                    {!isLast ? (
                      <motion.button
                        whileHover={{ x: 3 }} whileTap={{ scale: 0.96 }}
                        onClick={goNext}
                        className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-[11px] sm:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase"
                        style={{ background: C.goldSub, color: C.gold, border: `1px solid ${C.goldBorder}` }}
                      >
                        <span className="hidden sm:inline">Siguiente</span>
                        <span className="sm:hidden">Sig.</span>
                        <FiChevronRight size={13} />
                      </motion.button>
                    ) : (
                      <motion.button
                        whileHover={{ x: 3 }} whileTap={{ scale: 0.96 }}
                        onClick={goNext}
                        className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-[11px] sm:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase"
                        style={{ background: C.goldSub, color: C.gold, border: `1px solid ${C.goldBorder}` }}
                      >
                        Finalizar
                        <FiArrowRight size={13} />
                      </motion.button>
                    )}
                  </div>

                  {/* ── SECCIÓN INFERIOR: toggle + chips + guardar ── */}
                  <div className="mt-6 sm:mt-8 space-y-4">

                    <ActiveToggle
                      value={form.activo}
                      onChange={() => setForm((p) => ({ ...p, activo: p.activo ? 0 : 1 }))}
                    />

                    {/* Chips compactos */}
                    <CompletionChips steps={STEPS} completed={completed} />

                    {/* Botón guardar */}
                    <motion.button
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                      onClick={handleSave}
                      disabled={saveState === "saving"}
                      className="w-full flex items-center justify-center gap-2.5 py-3.5 sm:py-4 rounded-xl text-xs sm:text-sm font-light tracking-[0.2em] sm:tracking-[0.25em] uppercase shadow-xl transition-all"
                      style={{ background: saveBtn.bg, color: saveBtn.text }}
                    >
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={`icon-${saveState}`}
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                          transition={{ duration: 0.2 }}
                        >
                          {saveBtn.icon}
                        </motion.span>
                      </AnimatePresence>
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={`label-${saveState}`}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.2 }}
                        >
                          {saveBtn.label}
                        </motion.span>
                      </AnimatePresence>

                      {saveState === "idle" && !allRequiredDone && (
                        <span className="text-[9px] tracking-widest opacity-60">
                          ({requiredDone}/{requiredSteps.length} req.)
                        </span>
                      )}
                    </motion.button>

                    {!allRequiredDone && saveState === "idle" && (
                      <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="text-center text-[10px] font-light"
                        style={{ color: C.textFaint }}
                      >
                        Completá los campos requeridos antes de guardar
                      </motion.p>
                    )}
                  </div>

                  {/* Hint de teclado — solo desktop */}
                  <p className="hidden xl:block text-center text-[9px] tracking-widest uppercase mt-6 font-light"
                     style={{ color: C.textFaint }}>
                    ← → para navegar entre pasos
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAgenteIA;